import * as React from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";

import { modalOpened, modalClosed, ModalNames, Modal } from "../../actions/Modal";
import { getPortalId } from "./ModalPortal";
import { Overlay, Inner } from "./defaultElements";
import PopoverPortalProvider from "../popover/PopoverPortalProvider";
import { RootState } from "../../../state";

interface MappedProps {
  currentModal: Modal | null;
}

interface MappedActions {
  modalOpened: (args: Modal) => void;
  modalClosed: (args: Modal) => void;
}

export interface ModalRenderProps {
  onClose(): void;
}

export interface ModalChildrenProps {
  isOpen: boolean;
  onOpen(): void;
}

export type ModalRenderFunc = (props: ModalRenderProps) => React.ReactNode;
export type ModalChildrenFunc = (props: ModalChildrenProps) => React.ReactNode;

interface OwnProps {
  name: ModalNames;
  id?: string;
  portalName?: string;
  render: ModalRenderFunc;
  children?: ModalChildrenFunc;
}

type Props = MappedProps & MappedActions & OwnProps;

class ModalContainer extends React.Component<Props> {
  private portal?: HTMLElement;
  private innerRef?: HTMLElement;

  public componentDidMount() {
    if (typeof window !== "undefined") {
      const portalName = this.getPortalName(this.props);
      const portal = document.getElementById(getPortalId(portalName));
      if (!portal) {
        throw new Error(`ModalPortal element is not found. Please mount <ModalPortal name=\"${portalName}\" />`);
      }
      this.portal = portal;

      window.addEventListener("keydown", this.onKeyDownWindow);
    }
  }

  public componentDidUpdate(prevProps: Props) {
    const isOpen = Boolean(this.props.currentModal);
    const wasOpen = Boolean(prevProps.currentModal);
    if (isOpen === wasOpen) {
      return;
    }
    const html = document.documentElement;
    const body = document.body;
    if (isOpen) {
      html.style.overflowY = "visible";
      body.style.overflow = "hidden";
    } else {
      html.style.overflowX = "";
      body.style.overflow = "";
    }
  }

  public componentWillUnmount() {
    if (typeof window !== "undefined") {
      window.removeEventListener("keydown", this.onKeyDownWindow);
    }
  }

  public getPortalName(props: Props) {
    return props.portalName || "default";
  }

  public onOpen = () => {
    const { name, id, modalOpened } = this.props;
    modalOpened({ name, id });
  };

  public onClose = () => {
    const { name, id, modalClosed } = this.props;
    modalClosed({ name, id });
  };

  public onClickOverlay = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (this.innerRef && this.innerRef.contains(event.target as HTMLElement)) {
      // Do nothing
    } else {
      this.onClose();
    }
  };

  public onKeyDownWindow = (event: KeyboardEvent) => {
    // Listen ESC key event
    if (event.keyCode === 27) {
      this.onClose();
    }
  };

  public render() {
    const { name, id, currentModal, children } = this.props;
    const isOpen = !!currentModal && name === currentModal.name && id === currentModal.id;
    return (
      <>
        {children &&
          (children as ModalChildrenFunc)({
            onOpen: this.onOpen,
            isOpen,
          })}
        {this.portal &&
          createPortal(
            isOpen ? (
              <>
                <Overlay onClick={this.onClickOverlay}>
                  <Inner innerRef={ref => (this.innerRef = ref)}>
                    <PopoverPortalProvider name={`modal--${name}`}>
                      {this.props.render({
                        onClose: this.onClose,
                      })}
                    </PopoverPortalProvider>
                  </Inner>
                </Overlay>
              </>
            ) : (
              <div />
            ),
            this.portal
          )}
      </>
    );
  }
}

function mapStateToProps(state: RootState): MappedProps {
  return {
    currentModal: state.modals.currentModal,
  };
}

function mapDispatchToProps(dispatch: Dispatch<RootState>): MappedActions {
  return bindActionCreators(
    {
      modalOpened,
      modalClosed,
    },
    dispatch
  );
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { pure: false })(ModalContainer);



// WEBPACK FOOTER //
// ./frontend/src/common/components/modal/ModalContainer.tsx